import React from 'react';

import useHoverNextImage from '@/utils/hooks/useHoverNextImage';

import { getAsset, getContent } from '@/utils/helper';
import {
  getHttpsUrl,
  hasVideo,
  isVideo,
  isVideoWithPoster,
  getCloudinaryVideoPoster,
} from './Media.constants';

import { CtaWrapper } from '@/molecules/CTA/index';

import Image from './content/Image';
import { CloudinaryVideo, Video, VideoLightbox } from './content/Video';

// For multiple asset, render based on content source
export function DynamicAsset(props) {
  // Rendering Cloudinary?
  const isCloudinaryImage = !!props?.field?.[0]?.original_secure_url;
  if (isCloudinaryImage) {
    return <CloudinaryAsset {...props} />;
  }

  // Rendering Contentful?
  const fields = getContent(props.field?.sys?.id, props.entries)?.fields;
  const assets = [...([fields?.primaryAsset] ?? []), ...(fields?.asset ?? [])];
  if (fields === undefined) {
    return null;
  }
  return <ContentfulDynamicAsset {...props} asset={assets} />;
}

// 1. rendering cloudinary
export function CloudinaryAsset({
  field: assets,
  callToAction,
  lightbox = false,
  size,
  style,
  title,
  container,
  entries,
}) {
  const { index = 0, pauseImage, nextImage } = useHoverNextImage(assets);
  const cta = callToAction?.sys
    ? getContent(callToAction.sys.id, entries)?.fields?.url
    : callToAction;

  // if empty return null
  if (!assets) {
    return null;
  }

  if (assets?.length === 2 && hasVideo(assets) && lightbox) {
    const hasPoster = isVideoWithPoster(assets);
    const video = hasPoster ? assets[1] : assets[0];

    return (
      <VideoLightbox
        poster={assets?.[0]?.original_secure_url}
        size={size}
        style={style}
        title={title}
        content={
          <CloudinaryMedia
            title={title}
            container={container}
            lightbox={video}
            cta={cta}
            asset={assets?.[1]}
            size={size}
            style={style}
            controls
          />
        }
      />
    );
  }

  const renderAsset = (asset) => {
    if (!asset) return null;

    const isVideo = lightbox && asset.resource_type === 'video';
    const content = (
      <CloudinaryMedia
        title={title}
        container={container}
        lightbox={isVideo}
        controls={isVideo}
        cta={cta}
        asset={asset}
        size={size}
        style={style}
      />
    );
    return isVideo ? (
      <VideoLightbox
        poster={getCloudinaryVideoPoster(asset)}
        size={size}
        style={style}
        title={title}
        content={content}
      />
    ) : (
      content
    );
  };

  // hoverable next asset
  const canHover = assets?.length > 1;
  if (!canHover) {
    return renderAsset(assets[0]);
  }

  return (
    <div
      onMouseEnter={nextImage}
      onMouseOut={pauseImage}
      onMouseLeave={pauseImage}
    >
      {renderAsset(assets[index])}
    </div>
  );
}

export function Media({
  ctaUrl,
  ctaPage,
  contentType,
  controls,
  isCta,
  lightbox,
  mediaUrl,
  posterUrl,
  size,
  style,
  title,
  container,
}) {
  const httpsUrl = getHttpsUrl(mediaUrl);
  const posterHttpsUrl = posterUrl ? getHttpsUrl(posterUrl) : null;
  if (isVideo(contentType) && lightbox) {
    return (
      <VideoLightbox
        poster={posterHttpsUrl}
        size={size}
        style={style}
        title={title}
        videoUrl={httpsUrl}
        container={container}
      />
    );
  }

  const content = isVideo(contentType) ? (
    <Video
      sources={[
        {
          src: httpsUrl,
          contentType,
        },
      ]}
      videoProps={{
        autoPlay: true,
        loop: true,
        muted: !controls,
        controls,
        poster: posterHttpsUrl,
        style,
        size,
      }}
    />
  ) : (
    <Image
      title={title}
      url={httpsUrl}
      style={style}
      size={size}
      container={container}
    />
  );

  if (isCta) {
    return (
      <CtaWrapper
        url={ctaUrl}
        page={ctaPage}
        classNames="w-full h-full aaco-media"
      >
        {content}
      </CtaWrapper>
    );
  }

  return content;
}

export function CloudinaryMedia({
  asset,
  container,
  size,
  style,
  controls,
  cta,
  title,
  lightbox,
}) {
  const AssetContent = (
    <>
      {asset.resource_type === 'video' ? (
        <CloudinaryVideo
          vertical={container === 'full' && !lightbox}
          high={container === 'full' || !lightbox}
          asset={asset}
          videoProps={{
            muted: true,
            autoPlay: !controls,
            loop: !controls,
            playsInline: true,
            controls,
          }}
        />
      ) : (
        <Image
          url={asset}
          cloudinary={true}
          size={size}
          container={container}
          title={title}
          style={style}
        />
      )}
    </>
  );

  if (cta) {
    return (
      <CtaWrapper url={cta} classNames="w-full h-full aaco-media">
        {AssetContent}
      </CtaWrapper>
    );
  }

  return AssetContent;
}

// 2. Rendering contentful
function ContentfulDynamicAsset({
  assets,
  controls,
  callToAction = undefined,
  entries,
  asset,
  lightbox,
  size,
  style,
  usePrimaryAssetAsPoster,
  container,
  title,
}) {
  const assetFiles = asset?.length
    ? asset.map(({ sys } = {}) => getAsset(sys?.id, assets)?.fields)
    : null;
  const { index = 0, pauseImage, nextImage } = useHoverNextImage(assetFiles);

  if (!asset) return null;

  // video
  if (hasVideo(assetFiles) && (usePrimaryAssetAsPoster || lightbox)) {
    const hasPoster = isVideoWithPoster(assetFiles);
    const video = hasPoster ? assetFiles[1] : assetFiles[0];
    return (
      <Media
        contentType={video.file.contentType}
        controls={controls}
        lightbox={lightbox}
        mediaUrl={video.file.url}
        posterUrl={assetFiles?.[0]?.file?.url}
        size={size}
        style={`object-cover ${style}`}
        container={container}
      />
    );
  }

  const cta = callToAction?.sys
    ? getContent(callToAction.sys.id, entries)?.fields?.url
    : callToAction;
  // single asset
  if (assetFiles.length === 1) {
    return (
      <Media
        contentType={assetFiles[0]?.file?.contentType}
        lightbox={lightbox}
        ctaUrl={cta}
        isCta={!!cta}
        mediaUrl={assetFiles[0]?.file?.url}
        size={size}
        style={style}
        title={
          assetFiles[0]?.file?.title ?? assetFiles[0]?.description ?? title
        }
        container={container}
      />
    );
  }

  // hoverable next asset
  return (
    <div
      className="w-inherit h-inherit relative"
      onMouseEnter={nextImage}
      onMouseOut={pauseImage}
    >
      {assetFiles.map((asset, i) => (
        <div
          className={`absolute top-0 w-full h-full ${
            index !== i && 'opacity-0'
          }`}
          key={i}
        >
          <Media
            contentType={asset.file.contentType}
            mediaUrl={asset.file.url}
            size={size}
            style={style}
            title={asset.title}
            ctaUrl={cta}
            isCta={!!cta}
            container={container}
          />
        </div>
      ))}
    </div>
  );
}
